/**
 * ========================================
 * CUSTOM ASSET - countdown-modded.js
 * Version: 1.0
 * Created: 2026-10-14
 * Last Modified: 2026-10-14
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <countdown-modded> custom element (countdown-modded.liquid): a
 * yearly-repeating countdown. Reads data-month / data-day (no year) and
 * [data-end-of-day], computes the target in the VISITOR'S local time:
 * this year's date, or next year's once it has passed - so the counter
 * resets itself every year with no maintenance. Ticks once per second,
 * renders one box per digit (days grow to 3 digits when needed), and
 * updates a visually-hidden role="timer" line for screen readers once
 * per minute.
 *
 * MODIFICATIONS:
 * - v1.0 (2026-10-14): Initial creation.
 */

class CountdownModded extends HTMLElement {
  #interval = null;
  #lastAnnounced = -1;

  connectedCallback() {
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval) clearInterval(this.#interval);
    this.#interval = null;
  }

  get #target() {
    const month = parseInt(this.dataset.month, 10) || 12;
    const day = parseInt(this.dataset.day, 10) || 31;
    const endOfDay = this.hasAttribute('data-end-of-day');

    const now = new Date();
    for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
      const t = endOfDay
        ? new Date(year, month - 1, day, 23, 59, 59)
        : new Date(year, month - 1, day, 0, 0, 0);
      if (t > now) return t;
    }
    return null;
  }

  #tick() {
    const target = this.#target;
    if (!target) return;

    let diff = Math.max(0, target.getTime() - Date.now());

    const days = Math.floor(diff / 86400000);
    diff -= days * 86400000;
    const hours = Math.floor(diff / 3600000);
    diff -= hours * 3600000;
    const minutes = Math.floor(diff / 60000);
    diff -= minutes * 60000;
    const seconds = Math.floor(diff / 1000);

    this.#renderUnit('days', days, days > 99 ? 3 : 2);
    this.#renderUnit('hours', hours, 2);
    this.#renderUnit('minutes', minutes, 2);
    this.#renderUnit('seconds', seconds, 2);

    // Screen readers: announce once per minute, not every second.
    if (minutes !== this.#lastAnnounced) {
      this.#lastAnnounced = minutes;
      const announce = this.querySelector('[ref="announce"]');
      if (announce) announce.textContent = `${days} days, ${hours} hours, ${minutes} minutes remaining`;
    }
  }

  #renderUnit(name, value, width) {
    const el = this.querySelector(`[ref="${name}"]`);
    if (!el) return;

    const text = String(value).padStart(width, '0');
    const digits = el.children;

    if (digits.length !== text.length) {
      el.innerHTML = [...text]
        .map((ch) => `<span class="countdown-modded__digit">${ch}</span>`)
        .join('');
      return;
    }

    for (let i = 0; i < text.length; i++) {
      if (digits[i].textContent !== text[i]) digits[i].textContent = text[i];
    }
  }
}

if (!customElements.get('countdown-modded')) {
  customElements.define('countdown-modded', CountdownModded);
}
